/**
 * Design System - Base Colors
 * Defines the color palette used across all themes
 */

export const baseColors = {
  // Whites
  white: '#FFFFFF',
  whiteOpacity90: 'rgba(255, 255, 255, 0.9)',
  whiteOpacity80: 'rgba(255, 255, 255, 0.8)',
  whiteOpacity70: 'rgba(255, 255, 255, 0.7)',
  whiteOpacity60: 'rgba(255, 255, 255, 0.6)',
  whiteOpacity50: 'rgba(255, 255, 255, 0.5)',

  // Light Grays
  lightGray100: '#F5F5F5',
  lightGray200: '#EEEEEE',
  lightGray300: '#E0E0E0',
  lightGray400: '#BDBDBD',

  // Medium Grays
  mediumGray500: '#9E9E9E',
  mediumGray600: '#757575',
  mediumGray700: '#616161',

  // Dark Grays
  darkGray800: '#424242',
  darkGray900: '#212121',
  darkGrayAlmost: '#1A1A1A',
  black: '#000000',

  // SYN-2738 (A13): borda de campo de formulário, ≥3:1 sobre superfície clara
  // (#8E8E9A: 3.24:1 sobre branco, 3.09:1 sobre #F8FAFC). Temas escuros sobrescrevem.
  borderInput: '#8E8E9A',

  // Status Colors (universal)
  error: '#DC2626',
  errorLight: '#FEE2E2',
  success: '#16A34A',
  successLight: '#DCFCE7',
  warning: '#EA580C',
  warningLight: '#FFEDD5',
  info: '#0284C7',
  infoLight: '#E0F2FE',
};

/**
 * SynapSee Theme Colors
 * Primary: Red, Secondary: Blue, Accent: Amber
 */
export const synapseeColors = {
  ...baseColors,
  primary: '#E53935',
  primaryLight: '#EF5350',
  primaryDark: '#C62828',
  primaryOpacity20: 'rgba(229, 57, 53, 0.2)',

  secondary: '#1E88E5',
  secondaryLight: '#42A5F5',
  secondaryDark: '#1565C0',
  secondaryOpacity20: 'rgba(30, 136, 229, 0.2)',

  // Tertiary - Light gray for secondary buttons
  tertiary: '#F0F0F0',
  tertiaryLight: '#F5F5F5',
  tertiaryDark: '#E8E8E8',
  tertiaryOpacity20: 'rgba(240, 240, 240, 0.2)',

  accent: '#FFC107',
  accentLight: '#FFD54F',
  accentDark: '#FFA000',
  accentOpacity20: 'rgba(255, 193, 7, 0.2)',

  // Backgrounds
  backgroundLight: '#FFFFFF',
  backgroundDark: '#F8F8F8',
  surfaceLight: '#FFFFFF',
  surfaceElevated: '#F5F5F5',
};

/**
 * Indigo Dream Theme Colors
 * Primary: Deep Indigo, Secondary: Purple, Accent: Cyan
 */
export const indigoDreamColors = {
  ...baseColors,
  primary: '#4F46E5',
  primaryLight: '#6366F1',
  primaryDark: '#3730A3',
  primaryOpacity20: 'rgba(79, 70, 229, 0.2)',

  secondary: '#7C3AED',
  secondaryLight: '#A855F7',
  secondaryDark: '#5B21B6',
  secondaryOpacity20: 'rgba(124, 58, 237, 0.2)',

  // Tertiary - Light indigo/purple for secondary buttons
  tertiary: '#F3E8FF',
  tertiaryLight: '#F5EAFF',
  tertiaryDark: '#E9D5FF',
  tertiaryOpacity20: 'rgba(243, 232, 255, 0.2)',

  accent: '#06B6D4',
  accentLight: '#22D3EE',
  accentDark: '#0891B2',
  accentOpacity20: 'rgba(6, 182, 212, 0.2)',

  // Backgrounds
  backgroundLight: '#FFFFFF',
  backgroundDark: '#F8FAFC',
  surfaceLight: '#FFFFFF',
  surfaceElevated: '#F1F5F9',
};

/**
 * Flora Theme Colors
 * Primary: Vibrant Green, Dark backgrounds
 * Based on flora.ai design system
 */
export const floraColors = {
  ...baseColors,
  // SYN-2738 (A17): #2E7D32 dava 3.86:1 sobre #0A0A0A. #4CAF50: 7.12:1 sobre
  // #0A0A0A, 5.44:1 sobre #262626. Secundário tinha o mesmo verde, mesmo ajuste.
  // Fundo do botão (texto branco) continua no verde escuro: #2E7D32 = 5.13:1.
  primary: '#4CAF50',
  primaryLight: '#81C784',
  primaryDark: '#1B5E20',
  primaryButton: '#2E7D32',
  primaryOpacity20: 'rgba(76, 175, 80, 0.2)',

  secondary: '#4CAF50',
  secondaryLight: '#81C784',
  secondaryDark: '#1B5E20',
  secondaryButton: '#2E7D32',
  secondaryOpacity20: 'rgba(76, 175, 80, 0.2)',

  // Tertiary - Dark gray for secondary buttons (dark theme optimized)
  tertiary: '#333333',
  tertiaryLight: '#3A3A3A',
  tertiaryDark: '#2D2D2D',
  tertiaryOpacity20: 'rgba(51, 51, 51, 0.2)',

  accent: '#4DD0E1',
  accentLight: '#80DEEA',
  accentDark: '#00ACC1',
  accentOpacity20: 'rgba(77, 208, 225, 0.2)',

  // Backgrounds - Dark mode optimized (darker shade)
  backgroundLight: '#0F0F0F',
  backgroundDark: '#0A0A0A',
  surfaceLight: '#1A1A1A',
  surfaceElevated: '#262626',

  // Flora specific neutrals (adjusted for darker backgrounds)
  neutral100: '#FFFFFF',
  neutral200: '#E8E8E8',
  neutral300: '#C0C0C0',
  neutral400: '#8A8A8A',
  neutral500: '#707070',
  neutral600: '#585858',

  // SYN-2738 (A13): borda de campo, 3.51:1 sobre surface #1A1A1A.
  borderInput: '#707070',
};

/**
 * Figma Theme Colors
 * Primary: Purple (#7C3AED), Secondary: Blue (#3B82F6), Accent: Cyan (#06B6D4)
 * Refined palette with better semantic colors and contrast
 */
export const figmaColors = {
  ...baseColors,
  primary: '#7C3AED',
  primaryLight: '#A78BFA',
  primaryDark: '#5B21B6',
  primaryOpacity20: 'rgba(124, 58, 237, 0.2)',

  secondary: '#1D4ED8',
  secondaryLight: '#3B82F6',
  secondaryDark: '#1e40af',
  secondaryOpacity20: 'rgba(29, 78, 216, 0.2)',

  accent: '#06B6D4',
  accentLight: '#22D3EE',
  accentDark: '#0891B2',
  accentOpacity20: 'rgba(6, 182, 212, 0.2)',

  // Backgrounds - using semantic colors
  backgroundLight: '#FFFFFF',
  backgroundDark: '#F9FAFB',
  surfaceLight: '#FFFFFF',
  surfaceElevated: '#F3F4F6',
};

/**
 * Adobe Spectrum Theme Colors (Light Mode)
 * Primary: Blue (#0066CC), Secondary: Gray (#525252), Accent: Green (#118800)
 * Based on Adobe Spectrum 2 design system with refined semantic colors
 */
export const adobeColors = {
  ...baseColors,
  // Override base error/warning/info/success with Adobe Spectrum values
  error: '#E34850',
  errorLight: '#FDE8E9',
  success: '#118800',
  successLight: '#E6F5E0',
  warning: '#E08500',
  warningLight: '#FFF4E6',
  info: '#0055CC',
  infoLight: '#E8F2FF',

  primary: '#0066CC',
  primaryLight: '#4080FF',
  primaryDark: '#003BA3',
  primaryOpacity20: 'rgba(0, 102, 204, 0.2)',

  secondary: '#525252',
  secondaryLight: '#767676',
  secondaryDark: '#353535',
  secondaryOpacity20: 'rgba(82, 82, 82, 0.2)',

  accent: '#118800',
  accentLight: '#51B22F',
  accentDark: '#0A4D00',
  accentOpacity20: 'rgba(17, 136, 0, 0.2)',

  // Backgrounds - using semantic colors
  backgroundLight: '#FFFFFF',
  backgroundDark: '#FAFAFA',
  surfaceLight: '#FFFFFF',
  surfaceElevated: '#F5F5F5',
};

/**
 * Adobe Spectrum Theme Colors (Dark Mode)
 * Dark backgrounds with adjusted semantics for visibility
 * Primary: Light Blue (#4D9FFF), Secondary: Light Gray (#D4D4D4), Accent: Light Green (#51B22F)
 */
export const adobeDarkColors = {
  ...baseColors,
  // Override base error/warning/info/success with Adobe Spectrum dark values
  error: '#F56761',
  errorLight: '#FDD8D8',
  success: '#51B22F',
  successLight: '#C8E6C9',
  warning: '#FFA724',
  warningLight: '#FFE0B2',
  info: '#4D9FFF',
  infoLight: '#BBDEFB',

  // SYN-2738 (A17): #1565C0 dava 2.84:1 sobre #202020. #6AAFFF: 7.13:1 sobre
  // #202020, 4.98:1 sobre surfaceElevated #3A3A3A. O fundo do botão (texto branco)
  // continua no azul escuro: primaryButton #1565C0 = 5.75:1 com branco.
  primary: '#6AAFFF',
  primaryLight: '#2680E0',
  primaryDark: '#0D47A1',
  primaryButton: '#1565C0',
  primaryOpacity20: 'rgba(106, 175, 255, 0.2)',

  secondary: '#D4D4D4',
  secondaryLight: '#E8E8E8',
  secondaryDark: '#A0A0A0',
  secondaryOpacity20: 'rgba(212, 212, 212, 0.2)',

  accent: '#51B22F',
  accentLight: '#78C74A',
  accentDark: '#309C00',
  accentOpacity20: 'rgba(81, 178, 47, 0.2)',

  // Backgrounds - Dark mode
  backgroundLight: '#202020',
  backgroundDark: '#141414',
  surfaceLight: '#2D2D2D',
  surfaceElevated: '#3A3A3A',

  // SYN-2738 (A17): mediumGray500 #9E9E9E dava 4.25:1 sobre #3A3A3A. #A8A8A8 = 4.78:1.
  textTertiary: '#A8A8A8',

  // SYN-2738 (A13): borda de campo, 3.99:1 sobre #2D2D2D, 3.29:1 sobre #3A3A3A.
  borderInput: '#8A8A8A',
};

/**
 * Luciano Tardin Theme Colors
 * Primary: Red (#ED1C24), Secondary: Orange (#E97E27), Tertiary: Slate (#475569), Accent: Gray (#6B7280)
 */
export const lucianoTardinColors = {
  ...baseColors,
  primary: '#ED1C24',
  primaryLight: '#C93035',
  primaryDark: '#B8151B',
  primaryHover: '#D41920',
  primaryOpacity20: 'rgba(237, 28, 36, 0.14)',

  secondary: '#E97E27',
  secondaryLight: '#FDE9D4',
  secondaryDark: '#C26518',
  secondaryOpacity20: 'rgba(233, 126, 39, 0.14)',
  // SYN-2738 (A9): branco sobre #E97E27 dá 2.81:1 (reprova AA). Fundo do botão
  // secundário usa o mesmo laranja escurecido: #B1601E 4.60:1, hover #955119 6.05:1.
  secondaryButton: '#B1601E',
  secondaryButtonHover: '#955119',

  // Tertiary - Light gray for secondary elements
  tertiary: '#F0F0F0',
  tertiaryLight: '#F7F7F8',
  tertiaryDark: '#E0E0E2',
  tertiaryOpacity20: 'rgba(240, 240, 240, 0.5)',

  accent: '#6B7280',
  accentLight: '#E5E7EB',
  accentDark: '#4B5563',
  accentOpacity20: 'rgba(107, 114, 128, 0.14)',

  // Override semantic colors
  error: '#DC2626',
  errorLight: '#FEE2E2',
  success: '#16A34A',
  successLight: '#DCFCE7',
  warning: '#D97706',
  warningLight: '#FEF3C7',
  info: '#0284C7',
  infoLight: '#E0F2FE',

  // Backgrounds
  backgroundLight: '#E6E6EE',
  backgroundDark: baseColors.white,
  backgroundNeutral: '#F2F2F8',
  surfaceLight: baseColors.white,
  surfaceElevated: '#FAFAFF',

  // Text - add these for theme file usage
  darkGray900: '#131313',
  mediumGray600: '#52525B',
  // SYN-2738: era #84848F (2.98:1 sobre backgroundLight #E6E6EE, reprova AA).
  // #66666F dá 4.58:1 sobre #E6E6EE, 5.10:1 sobre #F2F2F8 e 5.68:1 sobre branco.
  // Único uso: textTertiary do tema synapsee-2026 (texto).
  mediumGray500: '#66666F',
  lightGray300: '#C8C8D4',
  // SYN-2738 (A13): borderDefault (#C8C8D4) dá 1.66:1 sobre branco; campos de
  // formulário usam borderInput (#8E8E9A, herdado de baseColors: 3.24:1 sobre
  // branco, 3.11:1 sobre #FAFAFF). lightGray300 segue pras divisórias decorativas.
  lightGray200: '#DDDDE8',
  lightGray400: '#E5E7EB',
  lightGray100: '#F2F2F8',
};
